import React, {
  useContext,
  createContext,
  useState,
  Dispatch,
  useEffect,
} from 'react'
import { ThemeProvider as SCThemeProvider } from 'styled-components'
import useDetectTheme from '../hooks/detectTheme'
import { DolaThemes } from '../styles/types'

export type UserThemePreferences = 'auto' | 'light' | 'dark'

interface ThemeContext {
  theme: DolaThemes
  userTheme: UserThemePreferences
  setUserTheme: Dispatch<UserThemePreferences>
}

const ThemeContext = createContext<ThemeContext>({
  theme: 'light',
  userTheme: 'auto',
  setUserTheme: () => {},
})

interface ThemeProviderProps {
  theme: {
    dark: {
      [key: string]: any
    }
    light: {
      [key: string]: any
    }
  }
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  theme: globalTheme,
}) => {
  const [hasDetectedTheme, setHasDetectedTheme] = useState(false)
  const [hasGotLocalConfig, setHasGotLocalConfig] = useState(false)
  const [userTheme, setUserTheme] = useState<UserThemePreferences>('auto')

  const detectedTheme =
    useDetectTheme(() => setHasDetectedTheme(true)) || 'light'

  useEffect(() => {
    try {
      const theme = localStorage.getItem('theme') as UserThemePreferences | null

      if (theme && theme !== userTheme) {
        setUserTheme(theme)
      }
    } catch (error) {
      console.warn(error)
    }

    setHasGotLocalConfig(true)
  }, [])

  useEffect(() => {
    if (hasGotLocalConfig && userTheme) {
      try {
        localStorage.setItem('theme', userTheme)
      } catch (error) {
        console.warn(error)
      }
    }
  }, [userTheme])

  const theme = userTheme === 'auto' ? detectedTheme : userTheme

  const themeStyles = globalTheme[theme]

  if (!hasDetectedTheme || !hasGotLocalConfig) {
    return null
  }

  return (
    <ThemeContext.Provider value={{ theme, userTheme, setUserTheme }}>
      <SCThemeProvider theme={themeStyles}>{children}</SCThemeProvider>
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
export default ThemeProvider
